/**
 * S3 Utilities - Centralized S3 key extraction and presigned URL handling
 * 
 * This module provides robust URL parsing for both virtual-hosted and path-style S3 URLs.
 */

import axios from '../services/axiosInstance';

/**
 * Parse S3 key from various input formats
 * 
 * Supports:
 * - Virtual-hosted: https://advancecash.s3.me-central-1.amazonaws.com/expense_attachments/...
 * - Path-style: https://s3.me-central-1.amazonaws.com/advancecash/expense_attachments/...
 * - Raw paths: "advancecash/expense_attachments/..." or "expense_attachments/..."
 * 
 * @param {string} input - URL, path, or key
 * @returns {string|null} Clean S3 key without bucket name
 */
export function parseS3KeyFromUrl(input) {
  if (!input) return null;

  try {
    const u = new URL(input);

    // 1) Virtual-hosted style: advancecash.s3.me-central-1.amazonaws.com/<key>
    const virtualHosted = u.hostname.match(/^([^.]+)\.s3[.-][a-z0-9-]+\.amazonaws\.com$/);
    if (virtualHosted) {
      const key = u.pathname.replace(/^\/+/, '').split('?')[0]; // Remove leading / and query string
      return key || null;
    }

    // 2) Path-style: s3.me-central-1.amazonaws.com/advancecash/<key>
    const pathStyle = u.hostname.match(/^s3[.-][a-z0-9-]+\.amazonaws\.com$/);
    if (pathStyle) {
      const parts = u.pathname.replace(/^\/+/, '').split('/');
      // parts[0] = bucket, rest = key
      if (parts.length >= 2) {
        const [, ...rest] = parts;
        return rest.join('/') || null;
      }
    }
  } catch {
    // Not a valid URL - might be a raw path
    const p = input.replace(/^\/+/, '');
    
    // Strip bucket name if present
    if (p.startsWith('advancecash/')) {
      return p.slice('advancecash/'.length);
    }
    
    return p || null;
  }

  return null;
}

/**
 * Get fresh presigned URL from backend
 * 
 * @param {object|string} fileOrUrl - File object with url/path fields, or direct URL/path string
 * @param {number} expiresIn - URL validity in seconds (default 3600 = 1 hour)
 * @returns {Promise<string|null>} Presigned URL or null
 */
export async function getFreshPresignedUrl(fileOrUrl, expiresIn = 3600) {
  try {
    // Extract raw input from various sources
    let raw = '';
    
    if (typeof fileOrUrl === 'string') {
      raw = fileOrUrl;
    } else if (typeof fileOrUrl === 'object' && fileOrUrl !== null) {
      raw = fileOrUrl.url || fileOrUrl.path || fileOrUrl.s3_path || fileOrUrl.s3_key || fileOrUrl.preview || '';
    }
    
    // Parse S3 key
    const key = parseS3KeyFromUrl(raw);
    
    if (!key) {
      console.error('No valid S3 key found from input');
      return null;
    }
    
    // Get fresh presigned URL from backend
    const response = await axios.get('/files/presign', {
      params: { key, expires_in: expiresIn }
    });
    
    if (response.data?.url) {
      return response.data.url;
    }
    
    return null;
    
  } catch (error) {
    console.error('Error getting presigned URL:', error);
    throw error;
  }
}

/**
 * Check if input has valid S3 key
 * 
 * @param {object|string} fileOrUrl - File object or URL string
 * @returns {boolean}
 */
export function hasValidS3Key(fileOrUrl) {
  if (typeof fileOrUrl === 'string') {
    return parseS3KeyFromUrl(fileOrUrl) !== null;
  }
  
  if (typeof fileOrUrl === 'object' && fileOrUrl !== null) {
    const raw = fileOrUrl.url || fileOrUrl.path || fileOrUrl.s3_path || fileOrUrl.s3_key || '';
    return parseS3KeyFromUrl(raw) !== null;
  }
  
  return false;
}
